/**
 * /consult — 家长招生咨询(公开页,演示原型)。
 * 不进 ProtectedRoute:家长无需任何账号,像逛学校官网一样发问。
 * 三轮左右后 AI 会自然询问孩子姓名/年龄 → 后端自动建档并可预约面试;
 * lead 卡片在聊天流里给家长一个明确的"已登记"确认。
 * 公开页面沿用 InviteAcceptPage 的惯例:文案内联中文,不走 i18n。
 */
import { useEffect, useRef, useState } from "react";

import { useTenant } from "@/tenants";

interface Msg {
  role: "user" | "assistant";
  content: string;
}

interface Lead {
  name: string;
  age: number;
  grade: string;
  interview_date: string;
}

const OPENING =
  "您好!我是招生咨询老师 😊 很高兴为您介绍我们学校。您可以问我课程设置、班级规模、入学流程等任何问题。";

export default function ConsultPage() {
  const tenant = useTenant();
  const [msgs, setMsgs] = useState<Msg[]>([{ role: "assistant", content: OPENING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [lead, setLead] = useState<Lead | null>(null);
  const [err, setErr] = useState("");
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, lead]);

  async function send() {
    const q = input.trim();
    if (!q || busy) return;
    setErr("");
    const next: Msg[] = [...msgs, { role: "user", content: q }];
    setMsgs(next);
    setInput("");
    setBusy(true);
    try {
      const r = await fetch("/api/school/public/consult/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // 开场白不发给模型(它是前端固定文案);其余历史全量携带。
        body: JSON.stringify({ messages: next.slice(1) }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail ?? `HTTP ${r.status}`);
      setMsgs((m) => [...m, { role: "assistant", content: body.reply }]);
      if (body.lead) setLead(body.lead as Lead);
    } catch (e) {
      setErr((e as Error).message || "网络异常,请稍后再试");
      setMsgs(next.slice(0, -1));
      setInput(q);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white flex flex-col items-center">
      <header className="w-full max-w-2xl px-6 pt-8 pb-4 flex items-center gap-3">
        <img src={tenant.brand.logoSrc} alt="" className="h-10 w-10" />
        <div>
          <h1 className="text-lg font-semibold text-slate-800">{tenant.displayName} · 招生咨询</h1>
          <p className="text-xs text-slate-500">在线答疑 · 到园面试预约</p>
        </div>
      </header>

      <main className="w-full max-w-2xl flex-1 px-6 pb-36">
        <div className="space-y-3">
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-sky-600 text-white rounded-br-md"
                    : "bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm"
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}

          {lead && (
            <div className="flex justify-start">
              <div className="max-w-[80%] rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm shadow-sm">
                <p className="font-medium text-emerald-800 mb-1">✓ 已为您登记</p>
                <p className="text-emerald-700">
                  {lead.name}({lead.age} 岁{lead.grade ? ` · ${lead.grade}` : ""})
                  {lead.interview_date
                    ? ` · 面试时间 ${lead.interview_date}`
                    : " · 面试时间待确认"}
                </p>
                <p className="mt-1 text-xs text-emerald-600">
                  招生老师会在 1 个工作日内与您联系确认。
                </p>
              </div>
            </div>
          )}

          {busy && (
            <div className="flex justify-start">
              <div className="rounded-2xl bg-white border border-slate-200 px-4 py-2.5 text-sm text-slate-400 shadow-sm">
                正在输入…
              </div>
            </div>
          )}
        </div>
        <div ref={bottomRef} />
      </main>

      <footer className="fixed bottom-0 w-full bg-white/90 backdrop-blur border-t border-slate-200">
        <div className="mx-auto max-w-2xl px-6 py-4">
          {err && <p className="mb-2 text-xs text-rose-600">{err}</p>}
          <div className="flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) void send();
              }}
              placeholder="想了解什么?例如:你们的英语课程是怎么安排的?"
              className="flex-1 rounded-full border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-sky-400"
            />
            <button
              onClick={() => void send()}
              disabled={busy || !input.trim()}
              className="rounded-full bg-sky-600 px-5 py-2.5 text-sm font-medium text-white disabled:opacity-40"
            >
              发送
            </button>
          </div>
          <p className="mt-2 text-center text-[11px] text-slate-400">
            演示环境 · 对话内容仅用于招生咨询与面试预约
          </p>
        </div>
      </footer>
    </div>
  );
}
